import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Card, Form, Row, Col, Button } from 'react-bootstrap';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import config from './config';

// Translations
const translations = {
  en: {
    floorTimer: 'Solar Floor Timer',
    unknownFloor: 'Unknown Floor',
    loading: 'Loading floor data...',
    error: 'Error',
    enableTimer: 'Enable Timer',
    floorOn: 'Floor Active',
    turnOnTime: 'Turn ON Time',
    turnOffTime: 'Turn OFF Time',
    to: 'to',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving...',
    noSettings: 'No settings found for this floor',
    cannotSave: 'Cannot save: No settings data available',
    settingsSaved: 'Settings saved successfully!',
    errorSaving: 'Error saving settings',
    noFloorGuid: 'No floor GUID provided in URL',
    apiFailed: 'API call failed',
    invalidGuid: 'Invalid GUID',
    errorParsingJson: 'Error parsing settings JSON',
  },
  nl: {
    floorTimer: 'Solar Floor Timer',
    unknownFloor: 'Onbekende Vloer',
    loading: 'Vloergegevens laden...',
    error: 'Fout',
    enableTimer: 'Timer Inschakelen',
    floorOn: 'Vloer Actief',
    turnOnTime: 'Inschakeltijd',
    turnOffTime: 'Uitschakeltijd',
    to: 'tot',
    cancel: 'Annuleren',
    save: 'Opslaan',
    saving: 'Opslaan...',
    noSettings: 'Geen instellingen gevonden voor deze vloer',
    cannotSave: 'Kan niet opslaan: Geen instellingsgegevens beschikbaar',
    settingsSaved: 'Instellingen succesvol opgeslagen!',
    errorSaving: 'Fout bij opslaan van instellingen',
    noFloorGuid: 'Geen vloer GUID in URL opgegeven',
    apiFailed: 'API-aanroep mislukt',
    invalidGuid: 'Ongeldige GUID',
    errorParsingJson: 'Fout bij verwerken van instellingen JSON',
  }
};

function TimerApp() {
  // Get URL parameters from React Router
  const { lang = 'en', guid } = useParams();
  
  // Get translations object only once
  const t = useMemo(() => {
    return translations[lang === 'nl' ? 'nl' : 'en'];
  }, [lang]);
  
  // Use the GUID from URL path
  const floorGUID = guid;
  
  const [floorData, setFloorData] = useState(null);
  const [floorSettings, setFloorSettings] = useState(null);
  const [settingsRecord, setSettingsRecord] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dataFetched, setDataFetched] = useState(false); // To prevent multiple fetches
  
  // Form state for timer settings with defaults from config
  const [timerEnabled, setTimerEnabled] = useState(config.defaults.timerEnabled);
  const [floorOn, setFloorOn] = useState(config.defaults.floorOn);
  const [onHours, setOnHours] = useState(config.defaults.onHours);
  const [onMinutes, setOnMinutes] = useState(config.defaults.onMinutes);
  const [offHours, setOffHours] = useState(config.defaults.offHours);
  const [offMinutes, setOffMinutes] = useState(config.defaults.offMinutes);
  const [originalSettings, setOriginalSettings] = useState(null);
  
  // Helper function for making REST calls - memoized with stable dependencies
  const apiCall = useCallback(async (endpoint, method = 'GET', body = null) => {
    if (!floorGUID) {
      throw new Error(t.noFloorGuid);
    }
    
    const baseUrl = config.api.baseUrl;
    const url = `${baseUrl}${endpoint}`;
    
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'floorGUID': floorGUID
      }
    };
    
    if (body) {
      options.body = JSON.stringify(body);
    }
    
    try {
      const response = await fetch(url, options);
      
      if (!response.ok) {
        if(response.status === 403) {
          throw new Error(`${t.invalidGuid}: ${floorGUID} [${response.status}]`);
        } else {
          throw new Error(`${t.apiFailed}: ${response.status}`);
        }
      }
      
      // For successful DELETE operations, some APIs may not return JSON
      if (method === 'DELETE' && response.status === 204) {
        return { success: true };
      }
      
      return await response.json();
    } catch (error) {
      console.error('API call error:', error);
      throw error;
    }
  }, [floorGUID, t]);
  
  // Format hours and minutes to HH:MM format for display
  const formatTimeDisplay = useCallback((hours, minutes) => {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }, []);
  
  // Handle hour input change
  const handleHourChange = useCallback((setter) => (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 0 && value <= 23) {
      setter(value);
    }
  }, []);
  
  // Handle minute input change
  const handleMinuteChange = useCallback((setter) => (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 0 && value <= 59) {
      setter(value);
    }
  }, []);
  
  // Fetch floor settings when floorData is available
  const fetchFloorSettings = useCallback(async () => {
    if (!floorData || !floorData.id) {
      return;
    }
    
    try {
      const floorId = floorData.id;
      const endpoint = config.api.endpoints.floorSettings.path
        + `?fields=${config.api.endpoints.floorSettings.fields}`
        + `&where=${config.api.endpoints.floorSettings.whereClause.replace('{floorId}', floorId)}`;
      
      const response = await apiCall(endpoint);
      
      // Extract only the first item from the list
      const settingsItem = response?.list?.[0] || null;
      
      if (settingsItem && settingsItem.settings) {
        try {
          // Store the entire settings record for later updates
          setSettingsRecord(settingsItem);
          
          // Parse the double-encoded JSON
          const decodedSettings = JSON.parse(settingsItem.settings);
          setFloorSettings(decodedSettings);
          
          // Extract timer settings
          if (decodedSettings && decodedSettings.gameselector) {
            const { 
              timerenabled, onhour, onmin, offhour, offmin, flooron,
              org_timerenabled, org_onhour, org_onmin, org_offhour, org_offmin 
            } = decodedSettings.gameselector;
            
            // Set form state
            setFloorOn(!!flooron);
            
            if (flooron && org_timerenabled !== undefined) {
              // If floor is on and we have backup values, keep them in state
              // but don't show them in UI (they'll be restored when floor is turned off)
              setTimerEnabled(!!org_timerenabled);
              setOnHours(org_onhour || config.defaults.onHours);
              setOnMinutes(org_onmin || config.defaults.onMinutes);
              setOffHours(org_offhour || config.defaults.offHours);
              setOffMinutes(org_offmin || config.defaults.offMinutes);
            } else {
              // Normal case - use the actual timer settings
              setTimerEnabled(!!timerenabled);
              setOnHours(onhour || config.defaults.onHours);
              setOnMinutes(onmin || config.defaults.onMinutes);
              setOffHours(offhour || config.defaults.offHours);
              setOffMinutes(offmin || config.defaults.offMinutes);
            }
            
            // Save original settings for cancel functionality
            setOriginalSettings({
              timerenabled: !!timerenabled,
              floorOn: !!flooron,
              onHours: onhour || config.defaults.onHours,
              onMinutes: onmin || config.defaults.onMinutes,
              offHours: offhour || config.defaults.offHours,
              offMinutes: offmin || config.defaults.offMinutes
            });
          }
        } catch (parseError) {
          console.error(t.errorParsingJson, parseError);
          setFloorSettings(null);
        }
      } else {
        console.log(t.noSettings);
        setFloorSettings(null);
      }
    } catch (err) {
      console.error('Error fetching floor settings:', err);
      // Don't update the main error state to avoid affecting the UI
    }
  }, [floorData, apiCall, t]);
  
  // Effect for fetching floor settings when floorData changes
  useEffect(() => {
    if (floorData) {
      fetchFloorSettings();
      
      // Update the document title with the floor name
      if (floorData.name) {
        document.title = `${floorData.name} - Apollios Timer`;
      }
    }
  }, [floorData, fetchFloorSettings]);
  
  // Initial data fetch - only run once
  useEffect(() => {
    // Only fetch data if it hasn't been fetched yet and we have a GUID
    if (!dataFetched && !floorGUID) {
      setError(t.invalidGuid);
      setLoading(false);
      setDataFetched(true);
    } else if (!dataFetched && floorGUID) {
      const fetchFloorData = async () => {
        try {
          setLoading(true);
          const endpoint = config.api.endpoints.floors.path
            + `?fields=${config.api.endpoints.floors.fields}`
            + `&where=${config.api.endpoints.floors.whereClause.replace('{GUID}', floorGUID)}`;
          
          const response = await apiCall(endpoint);
          
          // Extract only the first item from the list
          const floorItem = response?.list?.[0] || null;
          setFloorData(floorItem);
          setError(null);
        } catch (err) {
          setError(err.message);
          setFloorData(null);
          // Reset document title on error
          document.title = "Apollios Timer";
        } finally {
          setLoading(false);
          setDataFetched(true); // Mark data as fetched
        }
      };
      
      fetchFloorData();
    }
    
    // Cleanup function to reset document title when the component unmounts
    return () => {
      document.title = "Apollios Timer";
    };
  }, [apiCall, floorGUID, dataFetched, t]);
  
  // Determined if controls should be disabled
  const controlsDisabled = useCallback(() => {
    return floorOn || !timerEnabled;
  }, [floorOn, timerEnabled]);
  
  const handleSave = useCallback(async () => {
    if (!floorSettings || !settingsRecord || !floorData) {
      console.error(t.cannotSave);
      return;
    }
    
    try {
      setSaving(true);
      
      // Update the timer settings in the floor settings object
      const updatedFloorSettings = { ...floorSettings };
      
      if (!updatedFloorSettings.gameselector) {
        updatedFloorSettings.gameselector = {};
      }
      
      if (floorOn) {
        // When floor is on, force specific settings
        // First backup the original values if we're switching to floor on
        updatedFloorSettings.gameselector.org_timerenabled = timerEnabled;
        updatedFloorSettings.gameselector.org_onhour = onHours;
        updatedFloorSettings.gameselector.org_onmin = onMinutes;
        updatedFloorSettings.gameselector.org_offhour = offHours;
        updatedFloorSettings.gameselector.org_offmin = offMinutes;
        
        // Then set the forced values
        updatedFloorSettings.gameselector.timerenabled = true;
        updatedFloorSettings.gameselector.onhour = 4;
        updatedFloorSettings.gameselector.onmin = 0;
        updatedFloorSettings.gameselector.offhour = 4;
        updatedFloorSettings.gameselector.offmin = 1;
      } else {
        // Always use the current UI values when saving - these represent 
        // what the user wants, whether they came from backup or direct input
        updatedFloorSettings.gameselector.timerenabled = timerEnabled;
        updatedFloorSettings.gameselector.onhour = onHours;
        updatedFloorSettings.gameselector.onmin = onMinutes;
        updatedFloorSettings.gameselector.offhour = offHours;
        updatedFloorSettings.gameselector.offmin = offMinutes;
        
        // Clear any backup values since we're explicitly saving the current UI state
        delete updatedFloorSettings.gameselector.org_timerenabled;
        delete updatedFloorSettings.gameselector.org_onhour;
        delete updatedFloorSettings.gameselector.org_onmin;
        delete updatedFloorSettings.gameselector.org_offhour;
        delete updatedFloorSettings.gameselector.org_offmin;
      }
      
      // Always save the floor on state
      updatedFloorSettings.gameselector.flooron = floorOn;
      
      // Double-encode the settings JSON for sending back to the server
      const encodedSettings = JSON.stringify(updatedFloorSettings);
      
      // Step 1: Delete the existing record
      const deleteEndpoint = config.api.endpoints.floorSettings.path;
      await apiCall(deleteEndpoint, 'DELETE', { id: settingsRecord.id });
      
      // Step 2: Create a new record with updated settings
      const createEndpoint = config.api.endpoints.floorSettings.path;
      const createPayload = {
        floor: { id: floorData.id },
        status: { id: 1 }, // Assuming 1 is active status as per the whereClause
        settings: encodedSettings,
        version: settingsRecord.version ? parseInt(settingsRecord.version) + 1 : 1
      };
      
      const response = await apiCall(createEndpoint, 'POST', createPayload);
      
      if (response) {
        console.log(t.settingsSaved, response);
        
        // Update the local state with the new settings and record
        setFloorSettings(updatedFloorSettings);
        setSettingsRecord(response);
        
        // Update original settings to reflect the saved state
        setOriginalSettings({
          timerenabled: timerEnabled,
          floorOn: floorOn,
          onHours: onHours,
          onMinutes: onMinutes,
          offHours: offHours,
          offMinutes: offMinutes
        });
      }
    } catch (err) {
      console.error(`${t.errorSaving}:`, err);
      alert(`${t.errorSaving}: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }, [floorSettings, settingsRecord, floorData, timerEnabled, floorOn, onHours, onMinutes, offHours, offMinutes, apiCall, t]);
  
  const handleCancel = useCallback(() => {
    if (originalSettings) {
      setTimerEnabled(originalSettings.timerenabled);
      setFloorOn(originalSettings.floorOn);
      setOnHours(originalSettings.onHours);
      setOnMinutes(originalSettings.onMinutes);
      setOffHours(originalSettings.offHours);
      setOffMinutes(originalSettings.offMinutes);
    }
  }, [originalSettings]);
  
  return (
    <div className="app-background">
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <Card className="central-screen">
          <Card.Header className='headerCard'>
            <img src="/logo.webp" alt="Logo" id="logo"></img>
          </Card.Header>
          <Card.Body>
            <h1>
              {loading ? (
                t.loading
              ) : (
                `${floorData?.name || t.unknownFloor} - ${t.floorTimer}`
              )}
            </h1>
            {loading ? (
              <p>{t.loading}</p>
            ) : error ? (
              <p>{t.error}: {error}</p>
            ) : (
              <div>
                <Form className="mt-4">
                  <Form.Group className="mb-4">
                    <Form.Check 
                      type="switch"
                      id="floor-on"
                      label={t.floorOn}
                      checked={!floorOn}
                      onChange={(e) => setFloorOn(!e.target.checked)}
                      className="mb-3"
                    />
                    
                    <Form.Check 
                      type="switch"
                      id="timer-enabled"
                      label={t.enableTimer}
                      checked={timerEnabled}
                      onChange={(e) => setTimerEnabled(e.target.checked)}
                      className="mb-3"
                      disabled={floorOn}
                    />
                    
                    <Row className="align-items-center">
                      <Col xs={12} md={5}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t.turnOnTime}:</Form.Label>
                          <div className="time-input-group">
                            <input
                              type="number" 
                              min="0"
                              max="23"
                              value={onHours}
                              onChange={handleHourChange(setOnHours)}
                              disabled={controlsDisabled()}
                              className="hours-input"
                            />
                            <span className="time-separator">:</span>
                            <input
                              type="number"
                              min="0"
                              max="59"
                              value={onMinutes}
                              onChange={handleMinuteChange(setOnMinutes)}
                              disabled={controlsDisabled()}
                              className="minutes-input"
                            />
                            <span className="time-unit">h</span>
                          </div>
                        </Form.Group>
                      </Col>
                      
                      <Col xs={12} md={2} className="text-center my-2">
                        <span className="clock-separator">{t.to}</span>
                      </Col>
                      
                      <Col xs={12} md={5}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t.turnOffTime}:</Form.Label>
                          <div className="time-input-group">
                            <input
                              type="number"
                              min="0"
                              max="23"
                              value={offHours}
                              onChange={handleHourChange(setOffHours)}
                              disabled={controlsDisabled()}
                              className="hours-input"
                            />
                            <span className="time-separator">:</span>
                            <input
                              type="number"
                              min="0"
                              max="59"
                              value={offMinutes}
                              onChange={handleMinuteChange(setOffMinutes)}
                              disabled={controlsDisabled()}
                              className="minutes-input"
                            />
                            <span className="time-unit">h</span>
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Form.Group>
                  
                  <div className="d-flex justify-content-end mt-4">
                    <Button 
                      variant="secondary" 
                      className="me-2" 
                      onClick={handleCancel}
                      disabled={saving}
                    >
                      {t.cancel}
                    </Button>
                    <Button 
                      variant="primary" 
                      onClick={handleSave} 
                      disabled={saving}
                    >
                      {saving ? t.saving : t.save}
                    </Button>
                  </div>
                </Form>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/:lang/:guid" element={<TimerApp />} />
        <Route path="/:guid" element={<TimerApp />} />
        <Route path="/" element={<TimerApp />} />
      </Routes>
    </Router>
  );
}

export default App;